import { CmsMessage, getMenuList, HeaderMobileProps, HeaderMobileStateProps, MenuNavigationKey, MenuReactElement, MenuState, PriceView } from '@agoda/layout';
import { NavigationState } from 'store/slice';
import { HeaderEventHandlers } from 'component/common/Header/ActivitiesHeader.types';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { getLoginReturnUrl } from 'core/utils/activityUrlGenerator';
import { mapHeaderAnalyticsProps, mapHeaderCallbackProps, mapHeaderCoreProps, mapHeaderExperimentProps, mapHeaderFetchApiProps } from 'component/common/Header/header.selectors';
import { logError } from 'core-framework/util/LogMessage';
import { CommonHeaderProps } from 'core/type/header.types';
import { isHomePage } from 'core/constant/analytics/AnalyticsPageType';
import { getRightRenderMethod } from 'component/common/Header/GetRightRenderMethod/GetRightRenderMethod';
import { getShoppingCartBehavior } from 'core/utils/cart';
import { goBack } from 'store/slice/navigation';
import { getHeader } from 'core/window/windowService';
import { BehaviorManager } from '@behavior-management/core';
import InternalResolverFactory from 'behaviorManagement/resolverFactory';
import { ActivitiesBMProvider } from 'behaviorManagement/activitiesBMProvider';

const mapMobileHeaderStateProps = (state: NavigationState, config: BootstrapConfig, headerBaseProps: CommonHeaderProps): HeaderMobileStateProps => {
    const fetchApiProps = mapHeaderFetchApiProps(config);
    const loginReturnUrl = getLoginReturnUrl(state, config);

    return {
        ...fetchApiProps,
        ...headerBaseProps,
        ...headerBaseProps.mobile,
        loginReturnUrl,
        // @ts-ignore
        params: undefined,
        mobile: undefined,
        user: config.isLoggedIn ? headerBaseProps.user : undefined,
        menuState: MenuState.Closed,
        selectedPriceViewId: PriceView.TotalPricePerNight,
        browserDetails: config.browser.browserName,
    };
};

export const mapCmsMethod =
    (config: BootstrapConfig) =>
    (cmsId: number, fallbackCmsId?: number): string => {
        if (!config.cms[cmsId]) {
            logError(`CMS not configured for CMS ID ${cmsId}`, config.pageConfig.pageTypeId);
            return '';
        }
        if (fallbackCmsId && !config.cms[fallbackCmsId]) {
            logError(`CMS not configured for CMS ID ${fallbackCmsId}`, config.pageConfig.pageTypeId);
            return '';
        }
        return config.cms[cmsId] || (fallbackCmsId ? config.cms[fallbackCmsId] : '');
    };

export const formatUrlMethod = (url: string, urlType: MenuNavigationKey): string => {
    return url;
};

// https://gitlab.agodadev.io/full-stack/monoliths/agoda-com-spa-mobile/blob/master/Agoda.Mobile.Client/src/_migration/src/component/mobile/Menus/menuListMap.ts#L12
const mapMenuItems = (props: HeaderMobileProps, config: BootstrapConfig): MenuReactElement[] => {
    const cmsMethod = mapCmsMethod(config);
    const menuItems = getMenuList(props, cmsMethod, formatUrlMethod);
    return menuItems;
};

export const cmsProvider =
    (config: BootstrapConfig) =>
    (cms: CmsMessage): string => {
        const text = config.cms[cms.id];
        if (!text) {
            logError(`CMS not configured for CMS ID ${cms.id}`, config.pageConfig.pageTypeId);
            return '';
        }
        return text;
    };

export const mapMobileHeaderProps = (
    state: NavigationState,
    handlers: HeaderEventHandlers,
    headerProps: CommonHeaderProps,
    config: BootstrapConfig,
    menuState: MenuState,
    isAlwaysShowBackButton?: boolean,
): HeaderMobileProps => {
    const headerCoreProps = mapHeaderCoreProps(headerProps, config);
    const stateProps = mapMobileHeaderStateProps(state, config, headerProps);
    const callbackProps = mapHeaderCallbackProps(state, config, handlers);
    const fetchApiProps = mapHeaderFetchApiProps(config);
    const analyticsProps = mapHeaderAnalyticsProps(state.pageTypeId);
    // eslint-disable-next-line testing-library/render-result-naming-convention
    const renderRightHeaderItems = getRightRenderMethod(config?.behavior?.header?.menuItemType);
    const experimentProps = mapHeaderExperimentProps(config, headerProps);
    const isCartBehaviorEnabled = getShoppingCartBehavior(config);
    const activitiesBMProvider = new ActivitiesBMProvider();

    const props: HeaderMobileProps = {
        ...headerCoreProps,
        ...stateProps,
        ...callbackProps,
        ...fetchApiProps,
        ...analyticsProps,
        menuState,
        // @ts-ignore
        renderRightHeaderItems,
        isActivitiesEnabled: true,
        showCartIcon: isCartBehaviorEnabled && config.isLoggedIn,
        backButtonProps: {
            visible: isAlwaysShowBackButton ? true : !isHomePage(state.pageTypeId),
            hardNavigate: !isAlwaysShowBackButton,
            onClick: () => handlers.dispatch(goBack({ config, navigate: handlers.navigate })),
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        bltLogo: stateProps.bltLogo || {},
        isCartIngressEnable: isCartBehaviorEnabled,
        isNewBookingMenu: config.isLoggedIn,
        logoVariant: 'default',
        justifyContent: 'space-between',
        dataComponent: 'activities-header-mobile',
        defaultLanguage: config.language.cultureCode,
        cms: cmsProvider(config),
        experiments: {
            ...getHeader().experiments,
            ...experimentProps,
        },
        getBehaviorManager: () => new BehaviorManager(InternalResolverFactory(config, headerProps), activitiesBMProvider),
        isBlackHeader: config.behavior.header.isBlackHeaderBackground,
    };

    // This function builds the menu items for mobile header for target whitelabel
    props.children = mapMenuItems(props, config);
    return props;
};
